import { AbstractFile } from '@/models/files';
import { TemplateDetails } from './template-service';

export interface MessageCompositionState {
    messageContent: string;
    emailSubject: string;
    attachments: Array<AbstractFile>;
}

/**
 * Extracts the body content from a full HTML document.
 * If the HTML contains <body> tags, returns only the content inside them.
 * Otherwise, returns the original HTML unchanged.
 *
 * @param html - Full HTML document or HTML fragment
 * @returns Body content or original HTML
 */
export function extractBodyContent(html: string): string {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return bodyMatch ? bodyMatch[1] : html;
}

/**
 * Prepares message content when a template is applied to a reply.
 * Extracts the template body content and prepends it to the original reply body.
 *
 * @param templateDetails - The template details containing message content
 * @param originalReplyBody - The original reply conversation history
 * @returns The combined template content and reply history
 */
export function prepareReplyWithTemplate(
    templateDetails: TemplateDetails,
    originalReplyBody: string
): string {
    const templateBodyContent = extractBodyContent(templateDetails.messageContent);
    return templateBodyContent + originalReplyBody;
}

/**
 * Applies template details to message composition state.
 * Handles both new messages and replies differently.
 *
 * @param templateDetails - The template details to apply
 * @param originalReplyBody - Original reply body if this is a reply, empty string otherwise
 * @param currentSubject - Current email subject (preserved for replies)
 * @param defaultSubject - Default subject to use if current is empty
 * @param isEmail - Whether this is an email (affects attachment handling)
 * @returns Updated message composition state
 */
export function applyTemplateToMessage(
    templateDetails: TemplateDetails,
    originalReplyBody: string,
    currentSubject: string,
    defaultSubject: string,
    isEmail: boolean
): MessageCompositionState {
    const isReply = !!originalReplyBody;

    if (isReply) {
        return {
            messageContent: prepareReplyWithTemplate(templateDetails, originalReplyBody),
            emailSubject: currentSubject || defaultSubject,
            attachments: isEmail ? templateDetails.attachments : []
        };
    }

    return {
        messageContent: templateDetails.messageContent,
        emailSubject: templateDetails.emailSubject,
        attachments: isEmail ? templateDetails.attachments : []
    };
}

/**
 * Clears template selection and restores original message state.
 * Used when user clicks the clear button on template selector.
 *
 * @param originalReplyBody - Original reply body to restore, or empty string
 * @param defaultSubject - Default subject to restore, or empty string
 * @returns Restored message composition state
 */
export function clearTemplate(
    originalReplyBody: string,
    defaultSubject: string
): MessageCompositionState {
    return {
        messageContent: originalReplyBody || '',
        emailSubject: defaultSubject || '',
        attachments: []
    };
}
